import React, { useState, useEffect } from 'react';
import {
  Clock,
  MapPin,
  Camera,
  CheckCircle2,
  AlertCircle,
  UserCheck,
  Calendar,
  Sparkles,
  Timer,
  ChevronRight,
  Shield,
  Briefcase,
  History,
  FileSpreadsheet,
  Check,
  Info,
  LogOut,
  LogIn
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { AttendanceRecord } from '../types';

export const StaffAttendance: React.FC = () => {
  const { currentUser, attendanceRecords, clockIn, clockOut } = useApp();

  // Current real-time clock
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Filter & Form States
  const [selectedShift, setSelectedShift] = useState<{ name: string; time: string }>({
    name: 'กะเช้า (Morning Shift)',
    time: '07:00 - 19:00 น.',
  });
  const [workNotes, setWorkNotes] = useState('');
  const [workLocation, setWorkLocation] = useState('ป้อม รปภ. หลัก ประตู 1 (GPS Verified: 13.7563, 100.5018)');
  const [selfieUrl, setSelfieUrl] = useState(currentUser?.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&auto=format&fit=crop&q=80');
  const [isCapturingSelfie, setIsCapturingSelfie] = useState(false);
  const [filterStaff, setFilterStaff] = useState<'all' | 'my'>('all');
  const [actionNotice, setActionNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check if current user has an active clock-in today
  const todayStr = new Date().toISOString().split('T')[0];
  const myTodayRecord = attendanceRecords.find(
    (r) => (r.user_id === currentUser?.id || r.staff_name === currentUser?.full_name) && r.date === todayStr
  );

  const isClockedIn = Boolean(myTodayRecord && !myTodayRecord.clock_out_time);
  const isClockedOut = Boolean(myTodayRecord && myTodayRecord.clock_out_time);

  // Handle Clock In
  const handleClockIn = async () => {
    try {
      const rec = await clockIn({
        shift_name: selectedShift.name,
        shift_time: selectedShift.time,
        photo: selfieUrl,
        location: workLocation,
        notes: workNotes.trim() || undefined,
      });
      setActionNotice({
        type: 'success',
        text: `บันทึกเวลาเข้างานสำเร็จ! เวลา ${rec.clock_in_time} (${rec.shift_name})`,
      });
      setWorkNotes('');
    } catch (err) {
      setActionNotice({ type: 'error', text: 'เกิดข้อผิดพลาดในการลงเวลาเข้างาน' });
    }
  };

  // Handle Clock Out
  const handleClockOut = async () => {
    if (!myTodayRecord) return;
    try {
      const rec = await clockOut(myTodayRecord.id, {
        photo: selfieUrl,
        location: workLocation,
        notes: workNotes.trim() || undefined,
      });
      if (rec) {
        setActionNotice({
          type: 'success',
          text: `บันทึกเวลาออกงานเรียบร้อยแล้ว! เวลา ${rec.clock_out_time} (ชั่วโมงทำงานรวม: ${rec.work_duration_hours || 0} ชม.)`,
        });
        setWorkNotes('');
      }
    } catch (err) {
      setActionNotice({ type: 'error', text: 'เกิดข้อผิดพลาดในการลงเวลาออกงาน' });
    }
  };

  // Formatted date and time strings
  const timeFormatted = currentTime.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const dateFormatted = currentTime.toLocaleDateString('th-TH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  // Filtered records
  const displayRecords = filterStaff === 'my'
    ? attendanceRecords.filter((r) => r.user_id === currentUser?.id || r.staff_name === currentUser?.full_name)
    : attendanceRecords;

  // Stats calculation
  const totalMyRecords = attendanceRecords.filter(
    (r) => r.user_id === currentUser?.id || r.staff_name === currentUser?.full_name
  );
  const onTimeCount = totalMyRecords.filter((r) => r.clock_in_status === 'on_time').length;
  const completedCount = totalMyRecords.filter((r) => Boolean(r.clock_out_time)).length;

  return (
    <div id="staff-attendance-section" className="space-y-6">
      {/* Notice Banner */}
      {actionNotice && (
        <div
          className={`p-4 rounded-2xl text-xs flex items-center justify-between shadow-xs transition-all ${
            actionNotice.type === 'success'
              ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
              : 'bg-rose-50 border border-rose-200 text-rose-900'
          }`}
        >
          <div className="flex items-center gap-2.5">
            {actionNotice.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0" />
            )}
            <span className="font-medium text-sm">{actionNotice.text}</span>
          </div>
          <button
            onClick={() => setActionNotice(null)}
            className="text-slate-400 hover:text-slate-700 font-bold px-2"
          >
            ✕
          </button>
        </div>
      )}

      {/* Hero Attendance Clocking Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Real-time Clock & Status Panel */}
        <div className="lg:col-span-1 bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-950 text-white rounded-3xl p-6 shadow-lg flex flex-col justify-between relative overflow-hidden border border-slate-700">
          <div className="relative z-10 space-y-4">
            <div className="flex items-center justify-between">
              <span className="px-3 py-1 bg-white/10 rounded-full text-[11px] font-medium text-emerald-300 backdrop-blur-xs flex items-center gap-1.5">
                <Timer className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                ระบบลงเวลาพนักงานแบบเรียลไทม์
              </span>
              <span className="text-[11px] text-slate-300 font-mono">GPS ACTIVE</span>
            </div>

            {/* Big Digital Clock */}
            <div className="py-2">
              <div className="text-4xl sm:text-5xl font-black font-mono tracking-tight text-white">
                {timeFormatted}
              </div>
              <p className="text-emerald-200 text-xs sm:text-sm mt-1 font-medium flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-emerald-400" />
                {dateFormatted}
              </p>
            </div>

            {/* Current Staff Profile Tag */}
            <div className="bg-white/10 rounded-2xl p-3.5 backdrop-blur-xs border border-white/10 flex items-center gap-3">
              <img
                src={currentUser?.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&auto=format&fit=crop&q=80'}
                alt="Profile"
                className="w-11 h-11 rounded-xl object-cover border border-emerald-400/50 shrink-0"
              />
              <div className="overflow-hidden">
                <h4 className="font-bold text-white text-sm truncate">
                  {currentUser?.full_name || 'เจ้าหน้าที่ปฏิบัติการ'}
                </h4>
                <p className="text-[11px] text-emerald-200 truncate">
                  {currentUser?.department || 'ฝ่ายรักษาความปลอดภัยและบริการ'}
                </p>
              </div>
            </div>

            {/* Status of Today */}
            <div className="pt-2">
              <div className="text-xs text-slate-300 mb-1 font-medium">สถานะการทำงานวันนี้:</div>
              {isClockedOut ? (
                <div className="p-3 bg-emerald-500/20 border border-emerald-500/40 rounded-xl text-emerald-200 text-xs flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>บันทึกออกงานเรียบร้อยแล้ว ({myTodayRecord?.clock_out_time})</span>
                </div>
              ) : isClockedIn ? (
                <div className="p-3 bg-amber-500/20 border border-amber-500/40 rounded-xl text-amber-200 text-xs flex items-center gap-2 animate-pulse">
                  <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                  <span>กำลังปฏิบัติหน้าที่ (เข้างาน: {myTodayRecord?.clock_in_time})</span>
                </div>
              ) : (
                <div className="p-3 bg-slate-700/50 border border-slate-600 rounded-xl text-slate-300 text-xs flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-400 shrink-0" />
                  <span>ยังไม่ได้ลงเวลาเข้างานวันนี้</span>
                </div>
              )}
            </div>
          </div>

          <div className="relative z-10 pt-4 mt-4 border-t border-white/10 text-[11px] text-slate-400 flex items-center justify-between">
            <span>นราวดี อิลิแกนซ์ คลาวด์</span>
            <span>บันทึกพิกัดตำแหน่งอัตโนมัติ</span>
          </div>
        </div>

        {/* Action Panel: Clock In / Clock Out Form */}
        <div className="lg:col-span-2 bg-white rounded-3xl p-6 border border-slate-200 shadow-sm flex flex-col justify-between space-y-5">
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base sm:text-lg flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-emerald-600" />
                  บันทึกการลงเวลาปฏิบัติงาน (Time Attendance Punch)
                </h3>
                <p className="text-xs text-slate-500">
                  ระบุกะการทำงาน ยืนยันพิกัดสถานที่ และถ่ายภาพตนเองเพื่อบันทึกสถิติ
                </p>
              </div>

              {/* Shift badge */}
              <div className="self-start sm:self-auto px-3 py-1 bg-slate-100 rounded-xl text-xs font-semibold text-slate-700 font-mono">
                {selectedShift.time}
              </div>
            </div>

            {/* Shift Picker */}
            {!isClockedIn && !isClockedOut && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  เลือกกะการทำงาน (Work Shift)
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {[
                    { name: 'กะเช้า (Morning Shift)', time: '07:00 - 19:00 น.', desc: 'รปภ. และทีมสวน' },
                    { name: 'กะดึก (Night Shift)', time: '19:00 - 07:00 น.', desc: 'รปภ. ลาดตระเวน' },
                    { name: 'เวลานิติ (Office Shift)', time: '08:30 - 17:30 น.', desc: 'ผู้จัดการ / บัญชี / ช่าง' },
                  ].map((shift) => (
                    <button
                      key={shift.name}
                      type="button"
                      onClick={() => setSelectedShift({ name: shift.name, time: shift.time })}
                      className={`p-3 rounded-2xl text-left border transition-all ${
                        selectedShift.name === shift.name
                          ? 'border-emerald-600 bg-emerald-50/80 ring-2 ring-emerald-500/20 text-emerald-950 font-semibold'
                          : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                      }`}
                    >
                      <div className="text-xs font-bold">{shift.name}</div>
                      <div className="text-[11px] text-emerald-700 font-mono mt-0.5">{shift.time}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{shift.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Location & GPS Info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-rose-500" />
                  สถานที่ปฏิบัติการ / พิกัด GPS
                </label>
                <select
                  value={workLocation}
                  onChange={(e) => setWorkLocation(e.target.value)}
                  className="w-full px-3.5 py-2.5 text-xs border border-slate-300 rounded-xl bg-white focus:ring-2 focus:ring-emerald-500 outline-none text-slate-800"
                >
                  <option value="ป้อม รปภ. หลัก ประตู 1 (GPS: 13.7563, 100.5018)">
                    ป้อม รปภ. หลัก ประตู 1 (หน้าโครงการ)
                  </option>
                  <option value="สำนักงานนิติบุคคล อาคารสโมสร (GPS: 13.7568, 100.5022)">
                    สำนักงานนิติบุคคล อาคารสโมสร
                  </option>
                  <option value="ศูนย์ควบคุมกล้อง CCTV และงานช่าง (GPS: 13.7570, 100.5025)">
                    ศูนย์ควบคุมกล้อง CCTV และงานช่าง
                  </option>
                  <option value="ป้อม รปภ. ประตู 2 ด้านหลัง (GPS: 13.7558, 100.5030)">
                    ป้อม รปภ. ประตู 2 ด้านหลัง
                  </option>
                </select>
              </div>

              {/* Photo selfie capture simulation */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5 text-blue-500" />
                    ภาพถ่ายยืนยันตัวตน (Face Selfie)
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setIsCapturingSelfie(!isCapturingSelfie);
                      if (!isCapturingSelfie) {
                        setSelfieUrl('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&auto=format&fit=crop&q=80');
                      }
                    }}
                    className="text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold"
                  >
                    ถ่ายรูปใหม่
                  </button>
                </label>

                <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl border border-slate-200">
                  <img
                    src={selfieUrl}
                    alt="Selfie"
                    className="w-10 h-10 rounded-lg object-cover border border-slate-300 shrink-0"
                  />
                  <div className="text-[11px] text-slate-600 truncate flex-1">
                    <span className="font-semibold text-slate-800">ยืนยันใบหน้าสำเร็จ</span>
                    <p className="text-[10px] text-slate-400">ตรวจสอบความถูกต้องพร้อมลงเวลา</p>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded-md">
                    Verified
                  </span>
                </div>
              </div>
            </div>

            {/* Optional Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                หมายเหตุการปฏิบัติงาน (ถ้ามี)
              </label>
              <input
                type="text"
                value={workNotes}
                onChange={(e) => setWorkNotes(e.target.value)}
                placeholder="เช่น ปฏิบัติหน้าที่แทนผลัดเช้า, เบิกอุปกรณ์ตรวจเวรเรียบร้อย..."
                className="w-full px-3.5 py-2 text-xs border border-slate-300 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-slate-800"
              />
            </div>
          </div>

          {/* Action Buttons: Punch In or Punch Out */}
          <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Info className="w-4 h-4 text-emerald-600" />
              <span>ข้อมูลจะถูกซิงก์เข้าสู่ระบบบริหารจัดการนิติบุคคลส่วนกลาง</span>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto">
              {!isClockedIn && !isClockedOut && (
                <button
                  type="button"
                  onClick={handleClockIn}
                  className="w-full sm:w-auto px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  ลงเวลาเข้างาน (Clock In)
                </button>
              )}

              {isClockedIn && !isClockedOut && (
                <button
                  type="button"
                  onClick={handleClockOut}
                  className="w-full sm:w-auto px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs sm:text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  ลงเวลาออกงาน (Clock Out)
                </button>
              )}

              {isClockedOut && (
                <div className="px-4 py-2.5 bg-emerald-100 text-emerald-900 rounded-xl text-xs font-bold flex items-center gap-2">
                  <Check className="w-4 h-4 text-emerald-700" />
                  วันนี้บันทึกครบถ้วนแล้ว (เข้า: {myTodayRecord?.clock_in_time} | ออก: {myTodayRecord?.clock_out_time})
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="text-xs font-medium text-slate-500">วันทำงานทั้งหมด</div>
          <div className="mt-1 text-2xl font-bold text-slate-800">{totalMyRecords.length} วัน</div>
          <p className="text-[11px] text-emerald-600 mt-1">ประวัติในระบบโครงการ</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="text-xs font-medium text-slate-500">อัตราตรงเวลา</div>
          <div className="mt-1 text-2xl font-bold text-emerald-600">
            {totalMyRecords.length > 0 ? Math.round((onTimeCount / totalMyRecords.length) * 100) : 100}%
          </div>
          <p className="text-[11px] text-slate-400 mt-1">ไม่มีบันทึกการเข้าสาย</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="text-xs font-medium text-slate-500">ออกงานสมบูรณ์</div>
          <div className="mt-1 text-2xl font-bold text-blue-600">{completedCount} ครั้ง</div>
          <p className="text-[11px] text-slate-400 mt-1">ลงเวลาออกครบถ้วน</p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs">
          <div className="text-xs font-medium text-slate-500">สถานะกะงานวันนี้</div>
          <div className="mt-1 text-lg font-bold text-slate-800 truncate">
            {isClockedOut ? 'เสร็จสิ้นเวร' : isClockedIn ? 'ประจำการอยู่' : 'รอกะงาน'}
          </div>
          <p className="text-[11px] text-slate-400 mt-1">{selectedShift.name.split(' ')[0]}</p>
        </div>
      </div>

      {/* Attendance History Table */}
      <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xs">
        <div className="p-5 bg-slate-50/80 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-emerald-700" />
            <h4 className="font-bold text-slate-900 text-sm sm:text-base">
              สมุดบันทึกประวัติการลงเวลาเข้า-ออกงานเจ้าหน้าที่
            </h4>
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex p-1 bg-slate-200/70 rounded-xl text-xs font-medium">
              <button
                type="button"
                onClick={() => setFilterStaff('all')}
                className={`px-3 py-1 rounded-lg transition-all ${
                  filterStaff === 'all' ? 'bg-white text-slate-900 font-bold shadow-xs' : 'text-slate-600'
                }`}
              >
                เจ้าหน้าที่ทั้งหมด ({attendanceRecords.length})
              </button>
              <button
                type="button"
                onClick={() => setFilterStaff('my')}
                className={`px-3 py-1 rounded-lg transition-all ${
                  filterStaff === 'my' ? 'bg-white text-slate-900 font-bold shadow-xs' : 'text-slate-600'
                }`}
              >
                เฉพาะของฉัน ({totalMyRecords.length})
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50/60 text-slate-500 font-semibold border-b border-slate-200">
              <tr>
                <th className="p-3.5">เจ้าหน้าที่ / แผนก</th>
                <th className="p-3.5">วันที่</th>
                <th className="p-3.5">กะงาน</th>
                <th className="p-3.5">เวลาเข้างาน</th>
                <th className="p-3.5">เวลาออกงาน</th>
                <th className="p-3.5">ชั่วโมงทำงาน</th>
                <th className="p-3.5">พิกัดสถานที่</th>
                <th className="p-3.5">สถานะ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {displayRecords.map((rec) => (
                <tr key={rec.id} className="hover:bg-slate-50/70 transition-colors">
                  <td className="p-3.5">
                    <div className="flex items-center gap-2.5">
                      <img
                        src={rec.clock_in_photo || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80'}
                        alt="Staff"
                        className="w-8 h-8 rounded-lg object-cover border border-slate-200 shrink-0"
                      />
                      <div>
                        <p className="font-bold text-slate-900">{rec.staff_name}</p>
                        <p className="text-[10px] text-slate-500">{rec.department}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3.5 font-medium text-slate-700">
                    {new Date(rec.date).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })}
                  </td>
                  <td className="p-3.5">
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md font-medium text-[11px]">
                      {rec.shift_name}
                    </span>
                  </td>
                  <td className="p-3.5 font-mono font-bold text-emerald-700">
                    {rec.clock_in_time}
                  </td>
                  <td className="p-3.5 font-mono font-bold text-slate-700">
                    {rec.clock_out_time || <span className="text-amber-600 font-normal text-xs">ยังไม่ลงเวลาออก</span>}
                  </td>
                  <td className="p-3.5 font-mono text-slate-700">
                    {rec.work_duration_hours ? `${rec.work_duration_hours} ชม.` : '-'}
                  </td>
                  <td className="p-3.5 text-slate-600 max-w-[180px] truncate" title={rec.clock_in_location}>
                    {rec.clock_in_location || 'ป้อม รปภ. หลัก'}
                  </td>
                  <td className="p-3.5">
                    <span
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        rec.clock_in_status === 'on_time'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-rose-100 text-rose-800'
                      }`}
                    >
                      {rec.clock_in_status === 'on_time' ? 'ตรงเวลา' : 'สาย'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
